import React, { useState } from "react";

import AddNewContainer from "./PhysicalInventory/AddNewContainer";
import ContainerDetails from "./PhysicalInventory/ContainerDetails";

import {
  Container,
  InventoryOverrideRecord,
} from "../../../../reducers/componentSearch";
import { Part } from "../../../../reducers/models/parts";
import LoadingIcon from "../../../common/LoadingIcon";
import ErrorComponent from "../../../ErrorComponent";
import InventoryDetailsTable from "./InventoryDetailsTable";
import {
  InventoryContainer,
  InventoryDetailsContainer,
  OverrideHistoryRecordContainer,
  PriceBreaksDetailsContainer,
  TablesGroup,
} from "./InventoryStyles";
import { useUpdateSearch } from "../../../../utils/hooks/partSearch";
import InfoItem from "../PartDetails/InfoItem";
import {
  dateStringToHumanReadableDate,
  datetimeStringToHumanReadableDatetime,
  formatSmallCurrency,
} from "../../../../utils/valueFormatting";
import Button from "../../../common/Button";
import OverrideVirtualInventory from "./OverrideVirtualInventory";
import { getIdForPart } from "../../../../utils/parts";
import usePartInventory from "../../../../utils/hooks/inventory/partInventory";
import FlashPCBExpandable from "../../../common/FlashPCBCollapsible";
import { UserIdPill } from "../../../common/UserPill";
import {
  FormulaLine,
  InputLabel,
  ToolTitle,
} from "../../../../constants/Styles";
import { ToolContainer } from "../../../internal/FeederReport/FeederReportStyles";
import { PartDrawerInventorySubTitle } from "../../../internal/parts/InventoryUpdates/InventoryUpdatesStyles";
import ContainerHistoryTable from "../../../internal/parts/ContainerHistory";
import { usePaginatedContainerHistory } from "../../../../utils/hooks/inventory/containerHistory";
import ShipmentsUsingPart from "../../../internal/parts/ShipmentsUsingPart";
import InfoIcon from "../../../common/FlashPCBTooltip/InfoIcon";
import FlashPCBTooltip from "../../../common/FlashPCBTooltip";

const OverrideHistoryRecordDetails = ({
  inventoryOverrideRecord,
}: {
  inventoryOverrideRecord: InventoryOverrideRecord;
}) => {
  if (inventoryOverrideRecord.override !== null) {
    return (
      <OverrideHistoryRecordContainer>
        <InfoItem
          label={"Expiration"}
          value={dateStringToHumanReadableDate(
            inventoryOverrideRecord.override.expiration,
            true,
          )}
        />
        <InfoItem
          label={"Qty"}
          value={inventoryOverrideRecord.override.inventory_level}
        />
        <InfoItem
          label={"Minimum order quantity"}
          value={inventoryOverrideRecord.override.minimum_order_quantity.toLocaleString()}
        />
        {inventoryOverrideRecord.override.notes && (
          <InfoItem
            label={"Notes"}
            value={inventoryOverrideRecord.override.notes}
          />
        )}

        <InputLabel>Price breaks</InputLabel>
        <PriceBreaksDetailsContainer>
          {inventoryOverrideRecord.override.price_breaks.map((priceBreak) => {
            return (
              <InfoItem
                key={priceBreak.quantity}
                label={priceBreak.quantity.toLocaleString()}
                value={formatSmallCurrency(
                  priceBreak.unit_price,
                  priceBreak.currency,
                )}
              />
            );
          })}
        </PriceBreaksDetailsContainer>
      </OverrideHistoryRecordContainer>
    );
  } else {
    return (
      <>
        <FormulaLine>
          <pre>
            {inventoryOverrideRecord.snapshot
              ? JSON.stringify(
                  JSON.parse(inventoryOverrideRecord.snapshot),
                  undefined,
                  4,
                )
              : "Nothing to show"}
          </pre>
        </FormulaLine>
      </>
    );
  }
};

const Inventory = ({
  part,
  setIsUpdated,
}: {
  part: Part;
  setIsUpdated?: (arg: boolean) => void;
}) => {
  const { containerHistory } = usePaginatedContainerHistory(part.id as string);
  const partId = getIdForPart(part);
  const { inventoryResponse, refreshInventoryResponse, errored } =
    usePartInventory(partId);
  const [selectedContainer, setSelectedContainer] = useState<Container | null>(
    null,
  );
  const [addContainer, setAddContainer] = useState(false);
  const [overrideVirtualInventory, setOverrideVirtualInventory] =
    useState(false);

  const updateSearch = useUpdateSearch();

  const handleCancel = () => {
    setSelectedContainer(null);
    setAddContainer(false);
    setOverrideVirtualInventory(false);
  };

  const onFinishedEditing = () => {
    setIsUpdated?.(true);
    updateSearch({ updateSearchParameters: {} });
    refreshInventoryResponse();
    setSelectedContainer(null);
    setAddContainer(false);
    setOverrideVirtualInventory(false);
  };

  if (errored) {
    return <ErrorComponent smallTitle />;
  }

  if (!inventoryResponse) {
    return <LoadingIcon text={"Loading"} centerHorizontally />;
  }

  const renderPageElement = () => {
    if (addContainer) {
      return (
        <AddNewContainer
          part={part}
          partId={partId}
          onFinishedEditing={onFinishedEditing}
          onCancel={handleCancel}
          inventoryResponse={inventoryResponse}
        />
      );
    } else if (selectedContainer !== null) {
      return (
        <ContainerDetails
          selectedContainer={selectedContainer}
          partId={partId}
          onCancel={handleCancel}
          onFinishedEditing={onFinishedEditing}
        />
      );
    } else if (overrideVirtualInventory) {
      return (
        <OverrideVirtualInventory
          inventoryResponse={inventoryResponse}
          partId={partId}
          onCancel={handleCancel}
          onFinishedEditing={onFinishedEditing}
        />
      );
    } else {
      return (
        <>
          <InventoryDetailsContainer>
            <InfoItem
              label={
                <FlashPCBTooltip
                  content={
                    "Availability of parts may decrease under shorter project lead times"
                  }
                >
                  Quantity Available <InfoIcon />
                </FlashPCBTooltip>
              }
              value={inventoryResponse.inventory_level.toLocaleString()}
            />
            <InfoItem
              label={"Total last counted amount"}
              value={inventoryResponse.physical_inventory.last_counted_amount_total.toLocaleString()}
            />
            <InfoItem
              label={"Total active allocations"}
              value={inventoryResponse.physical_inventory.active_allocations_total.toLocaleString()}
            />
            <InfoItem
              label={"Total spent allocations"}
              value={inventoryResponse.physical_inventory.spent_allocations_total.toLocaleString()}
            />
            {inventoryResponse.price_info !== null && (
              <InfoItem
                label={"Unit Price"}
                value={formatSmallCurrency(
                  inventoryResponse.price_info.unit_price,
                  inventoryResponse.price_info.currency,
                )}
              />
            )}
            <InfoItem
              label={"Reel width (mm)"}
              value={part.reel_details.reel_width_mm || "Unknown"}
            />
            <InfoItem
              label={"Reel pitch (mm)"}
              value={part.reel_details.reel_pitch_mm || "Unknown"}
            />
          </InventoryDetailsContainer>
          <Button
            label={"Override virtual inventory"}
            isSmallSize
            onClick={() => setOverrideVirtualInventory(true)}
          />
          {inventoryResponse.virtual_inventory.override_history.map(
            (history) => {
              const title = `override from ${datetimeStringToHumanReadableDatetime(history.datetime_set)}`;
              return (
                <FlashPCBExpandable
                  key={history.datetime_set}
                  renderTrigger={(collapsibleOpen) =>
                    collapsibleOpen ? `Hide ${title}` : `See ${title}`
                  }
                >
                  <ToolContainer>
                    <ToolTitle>Override history record</ToolTitle>
                    <div>
                      <InputLabel>Created by</InputLabel>
                      <UserIdPill userId={history.user_id} />
                    </div>
                    {
                      <OverrideHistoryRecordDetails
                        inventoryOverrideRecord={history}
                      />
                    }
                  </ToolContainer>
                </FlashPCBExpandable>
              );
            },
          )}
          <Button
            label={"Add container"}
            isSmallSize
            onClick={() => setAddContainer(true)}
          />
          <TablesGroup>
            <div>
              <PartDrawerInventorySubTitle>
                Inventory details
              </PartDrawerInventorySubTitle>
              <InventoryDetailsTable
                inventoryResponse={inventoryResponse}
                partId={partId}
                onFinishedEditing={onFinishedEditing}
                onSelectContainer={(container: Container) => {
                  setSelectedContainer(container);
                }}
              />
            </div>
            <div>
              <PartDrawerInventorySubTitle>
                Shipments that used or are using part
              </PartDrawerInventorySubTitle>
              <ShipmentsUsingPart partId={partId} />
            </div>
            <div>
              <PartDrawerInventorySubTitle>
                Part history
              </PartDrawerInventorySubTitle>
              <ContainerHistoryTable containerHistory={containerHistory} />
            </div>
          </TablesGroup>
        </>
      );
    }
  };

  return <InventoryContainer>{renderPageElement()}</InventoryContainer>;
};

export default Inventory;
